import type {
  CubeId,
  CubeInfo,
  CubeLinesIds,
  FaultInfo,
  Survey2dInfo,
  UploadId,
} from '@core/core/types'
import type { HorizonId, ProjectId } from 'common/types'

import { constructClientDispenser } from '../clientDispenserFactory'
import { axiosInstance } from '../common'
import { CubeClient } from './__generated__/Cube'
import { FaultClient } from './__generated__/Fault'
import { HttpClient } from './__generated__/http-client'
import { ProjectCubeClient } from './__generated__/ProjectCube'
import { ProjectCubeLineClient } from './__generated__/ProjectCubeLine'
import { ProjectFaultClient } from './__generated__/ProjectFault'
import { ProjectHorizonClient } from './__generated__/ProjectHorizon'
import { ProjectSurvey2DClient } from './__generated__/ProjectSurvey2D'
import { Survey2DClient } from './__generated__/Survey2D'
import {
  convertToCubeInfo,
  convertToCubeLinesIds,
  convertToFaultInfo,
  convertToSurvey2d,
} from './converters'

const Clients = {
  CubeClient,
  FaultClient,
  ProjectCubeClient,
  ProjectCubeLineClient,
  ProjectFaultClient,
  ProjectSurvey2DClient,
  Survey2DClient,
  ProjectHorizonClient,
} as const

const getClient = constructClientDispenser(Clients, HttpClient, axiosInstance)

/// cubes
export const getCubeInfoByIdApi = (cubeId: CubeId | UploadId): Promise<CubeInfo> => {
  const client = getClient('CubeClient')
  return client.cubeGetCubeById(cubeId).then(convertToCubeInfo)
}

export const getAllCubesApi = (): Promise<CubeInfo[]> => {
  const client = getClient('CubeClient')
  return client.cubeGetAllCubes().then(cubeInfos => cubeInfos.map(convertToCubeInfo))
}

export const getCubesByProjectIdApi = (projectId: ProjectId): Promise<CubeInfo[]> => {
  const client = getClient('ProjectCubeClient')
  return client
    .projectCubeGetCubesByProjectId(projectId)
    .then(cubeInfos => cubeInfos.map(convertToCubeInfo))
}

/// faults

export const getFaultsByProjectIdApi = (projectId: ProjectId): Promise<FaultInfo[]> => {
  const client = getClient('ProjectFaultClient')
  return client
    .projectFaultGetFaultsByProjectId(projectId)
    .then(cubeInfos => cubeInfos.map(convertToFaultInfo))
}

/// horizons

/// 2d surveys

export const getSurvey2dsByProjectIdApi = (projectId: ProjectId): Promise<Survey2dInfo[]> => {
  const client = getClient('ProjectSurvey2DClient')
  return client
    .projectSurvey2DGetSurveys2DByProjectId(projectId)
    .then(surveys => surveys.map(convertToSurvey2d))
}

/// project
export const addProjectCubeApi = (projectId: ProjectId, cubeId: CubeId): Promise<void> => {
  const client = getClient('ProjectCubeClient')
  return client.projectCubeAddCubeToProject(projectId, { cubeId })
}

/// projectCubeLine
export const getCubeLinesIdsByProjectIdApi = (
  cubeId: CubeId,
  projectId: ProjectId,
): Promise<CubeLinesIds> => {
  const client = getClient('ProjectCubeLineClient')
  return client
    .projectCubeLineGetProjectCubeLinesByProjectId(projectId, cubeId)
    .then(convertToCubeLinesIds)
}

export const copyHorizonApi = (projectId: ProjectId, horizonId: HorizonId): Promise<string> => {
  const client = getClient('ProjectHorizonClient')
  return client.projectHorizonCreateCopyHorizon(projectId, horizonId)
}
